import { isCreditorNumber, isDebitorNumber, isKnownAccount } from './chartOfAccounts';
import { effectiveTaxCode, marginBase, marginVat, revenueAccountFor, taxCodeDefinition, taxCodeForRate, type TaxCode } from './taxCodes';
import type { Invoice, InvoiceLineKind } from './types';
import type { IncomingInvoice } from './incoming/types';

/**
 * Buchungssätze — the accounting rows behind both the CSV and the DATEV/EXTF export.
 *
 * Replaces an export that fabricated data: it emitted ONE row per invoice with a rate derived as
 * tax/net (a 19%+7% invoice became "15%"), booked everything to a hardcoded 8400 regardless of
 * rate, and expressed credit notes as negative amounts. Here: one row PER TAX CODE, the account
 * comes from the tax table, and direction is carried by the Soll/Haben-Kennzeichen with the
 * Umsatz always positive — which is what DATEV expects.
 *
 * DATEV convention used throughout: S/H refers to the KONTO field.
 *   Ausgangsrechnung  Debitor an Erlös    → Konto=Debitor,  Gegenkonto=Erlöskonto, S
 *   Gutschrift        Erlös an Debitor    → Konto=Debitor,  Gegenkonto=Erlöskonto, H
 *   Eingangsrechnung  Aufwand an Kreditor → Konto=Aufwand,  Gegenkonto=Kreditor,   S
 */
export type SollHaben = 'S' | 'H';

export interface BookingRow {
  readonly belegdatum: string;
  /** Belegfeld 1 — the document number the Fibu references. */
  readonly belegfeld1: string;
  /** Always positive; direction lives in sollHaben. */
  readonly umsatz: number;
  readonly sollHaben: SollHaben;
  readonly konto: string;
  readonly gegenkonto: string;
  readonly buSchluessel: string;
  readonly taxCode: TaxCode;
  readonly steuersatz: number;
  readonly buchungstext: string;
  readonly kost1: string;
  readonly richtung: 'Debitor' | 'Kreditor';
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Belegfeld 1 is limited and must not carry separators. */
function belegfeld(value: string): string {
  return value.replace(/[^0-9A-Za-z\-+.,$%&*]/g, '').slice(0, 36);
}

function buchungstext(value: string): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, 60);
}

export interface TaxGroup {
  readonly taxCode: TaxCode;
  readonly lineKind: InvoiceLineKind;
  readonly net: number;
  readonly tax: number;
  readonly gross: number;
}

/**
 * Splits an invoice into one group per (revenue account, tax code) — the unit a booking row is
 * made of. Grouping by the ACCOUNT (not the raw line kind) keeps freight lines that share the
 * 8409 account in one row while separating rates, which is exactly what the Fibu wants to see.
 * Returns null for a line whose rate is not a German rate, so the caller can refuse it.
 */
export function groupInvoiceByTax(invoice: Invoice): readonly TaxGroup[] | null {
  const groups = new Map<string, { taxCode: TaxCode; lineKind: InvoiceLineKind; net: number; tax: number }>();
  for (const line of invoice.lines) {
    const taxCode = effectiveTaxCode(line);
    if (!taxCode) return null;
    const account = revenueAccountFor(line.kind, taxCode);
    if (!account) return null;
    const definition = taxCodeDefinition(taxCode);
    const key = `${account}|${taxCode}`;
    const bucket = groups.get(key) ?? { taxCode, lineKind: line.kind, net: 0, tax: 0 };
    bucket.net += line.netAmount;
    // Differenzbesteuerung: die Steuer steckt in der MARGE, nicht im Rechnungsbetrag -- und sie
    // wird herausgerechnet, nicht aufgeschlagen. Der Umsatz bleibt der volle Verkaufspreis.
    bucket.tax += definition.marginScheme
      ? marginVat(line, definition.marginRate)
      : line.netAmount * (definition.rate / 100);
    groups.set(key, bucket);
  }
  return [...groups.values()].map((bucket) => ({
    taxCode: bucket.taxCode,
    lineKind: bucket.lineKind,
    net: round2(bucket.net),
    tax: round2(bucket.tax),
    // Bei der Margenbesteuerung ist der Rechnungsbetrag schon der Bruttobetrag -- die Steuer
    // steckt darin. Sie noch einmal aufzuschlagen waere doppelt.
    gross: taxCodeDefinition(bucket.taxCode).marginScheme
      ? round2(bucket.net)
      : round2(bucket.net + bucket.tax),
  }));
}

/** Booking rows for one outgoing document — one per revenue account and tax code. */
export function bookingRowsForInvoice(invoice: Invoice): readonly BookingRow[] {
  const groups = groupInvoiceByTax(invoice);
  if (!groups) return [];
  const isCredit = invoice.kind === 'credit_note';
  const text = isCredit
    ? `Gutschrift zu ${invoice.creditedInvoiceNumber ?? ''}`
    : `Rechnung ${invoice.customerName}`;

  return groups.flatMap((group) => {
    const account = revenueAccountFor(group.lineKind, group.taxCode);
    if (!account) return [];
    return [{
      belegdatum: invoice.issueDate ?? invoice.createdAt.slice(0, 10),
      belegfeld1: belegfeld(invoice.invoiceNumber ?? invoice.proposalNumber),
      umsatz: group.gross,
      // A credit note reverses the original booking: same accounts, opposite side.
      sollHaben: isCredit ? 'H' : 'S',
      konto: invoice.customerNumber,
      gegenkonto: account,
      buSchluessel: taxCodeDefinition(group.taxCode).datevBuKey,
      taxCode: group.taxCode,
      steuersatz: taxCodeDefinition(group.taxCode).rate,
      buchungstext: buchungstext(text),
      // KOST1 = Code der Beleg-Kostenstelle (Stufe 2b); leer, solange keine gesetzt ist.
      kost1: invoice.costCenterCode ?? '',
      richtung: 'Debitor' as const,
    }];
  });
}

/** Booking row for one incoming invoice — the Kontierung decides the expense account. */
export function bookingRowsForIncoming(invoice: IncomingInvoice): readonly BookingRow[] {
  const taxCode = taxCodeForRate(invoice.taxRate);
  if (!taxCode || !invoice.accountNumber) return [];
  return [{
    belegdatum: invoice.invoiceDate,
    belegfeld1: belegfeld(invoice.supplierInvoiceNumber || invoice.number),
    umsatz: round2(invoice.grossAmount),
    sollHaben: 'S',
    konto: invoice.accountNumber,
    gegenkonto: invoice.supplierNumber,
    buSchluessel: taxCodeDefinition(taxCode).datevBuKey,
    taxCode,
    steuersatz: taxCodeDefinition(taxCode).rate,
    buchungstext: buchungstext(invoice.bookingText || invoice.supplierName),
    kost1: invoice.costCenter,
    richtung: 'Kreditor',
  }];
}

/** Only documents that may legally be handed over: an RE/GS number exists and nothing is cancelled. */
export function exportableInvoices(invoices: readonly Invoice[]): readonly Invoice[] {
  return invoices.filter((invoice) => invoice.invoiceNumber && invoice.documentStatus !== 'cancelled');
}

export function exportableIncoming(incoming: readonly IncomingInvoice[]): readonly IncomingInvoice[] {
  return incoming.filter((invoice) => invoice.status !== 'rejected' && invoice.status !== 'cancelled');
}

export function buildBookingRows(
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
): readonly BookingRow[] {
  return [
    ...exportableInvoices(invoices).flatMap(bookingRowsForInvoice),
    ...exportableIncoming(incoming).flatMap(bookingRowsForIncoming),
  ];
}

// ---------------------------------------------------------------------------
// Validation — nothing leaves the building unchecked.
// ---------------------------------------------------------------------------

export type ExportIssueSeverity = 'error' | 'warning';

export interface ExportIssue {
  readonly severity: ExportIssueSeverity;
  readonly documentNumber: string;
  readonly party: string;
  readonly message: string;
}

/**
 * Everything that would make the handover wrong, found BEFORE it is written. An 'error' blocks the
 * export outright: the tax advisor books from this file, so a bad row is worse than no file.
 */
export function validateForExport(
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
): readonly ExportIssue[] {
  const issues: ExportIssue[] = [];

  for (const invoice of exportableInvoices(invoices)) {
    const number = invoice.invoiceNumber ?? invoice.proposalNumber;
    const party = invoice.customerName;

    for (const line of invoice.lines) {
      const taxCode = effectiveTaxCode(line);
      if (!taxCode) {
        issues.push({
          severity: 'error', documentNumber: number, party,
          message: `Position „${line.description}" hat ${line.vatRate} % Umsatzsteuer – das ist kein gültiger deutscher Steuersatz (0, 7 oder 19).`,
        });
        continue;
      }
      if (!revenueAccountFor(line.kind, taxCode)) {
        issues.push({
          severity: 'error', documentNumber: number, party,
          message: `Für „${line.description}" (${taxCodeDefinition(taxCode).label}) ist kein Erlöskonto hinterlegt.`,
        });
      }
    }

    if (!isDebitorNumber(invoice.customerNumber)) {
      issues.push({
        severity: 'error', documentNumber: number, party,
        message: `Kundennummer ${invoice.customerNumber} liegt nicht im Debitoren-Nummernkreis (10000–69999).`,
      });
    }

    if (invoice.lines.length === 0) {
      issues.push({ severity: 'error', documentNumber: number, party, message: 'Die Rechnung hat keine Positionen.' });
    }

    const groups = groupInvoiceByTax(invoice);
    if (groups) {
      // The rows must add up to the document, or the Fibu receives a different invoice than the customer.
      const gross = round2(groups.reduce((sum, group) => sum + group.gross, 0));
      if (Math.abs(gross - round2(invoice.grossTotal)) > 0.02) {
        issues.push({
          severity: 'error', documentNumber: number, party,
          message: `Die Buchungssätze ergeben ${gross.toFixed(2)} €, die Rechnung lautet auf ${invoice.grossTotal.toFixed(2)} €.`,
        });
      }
    }
  }

  for (const invoice of exportableIncoming(incoming)) {
    const party = invoice.supplierName;
    if (!invoice.accountNumber) {
      issues.push({
        severity: 'error', documentNumber: invoice.number, party,
        message: 'Kein Sachkonto kontiert – der Beleg fehlt sonst im Buchungsstapel.',
      });
    } else if (!isKnownAccount(invoice.accountNumber)) {
      issues.push({
        severity: 'error', documentNumber: invoice.number, party,
        message: `Sachkonto ${invoice.accountNumber} gibt es im Kontenplan nicht.`,
      });
    }

    if (!taxCodeForRate(invoice.taxRate)) {
      issues.push({
        severity: 'error', documentNumber: invoice.number, party,
        message: `${invoice.taxRate} % Vorsteuer ist kein gültiger deutscher Steuersatz.`,
      });
    }

    if (!isCreditorNumber(invoice.supplierNumber)) {
      issues.push({
        severity: 'error', documentNumber: invoice.number, party,
        message: `Lieferantennummer ${invoice.supplierNumber} liegt nicht im Kreditoren-Nummernkreis (70000–99999).`,
      });
    }

    if (!invoice.costCenter) {
      issues.push({
        severity: 'warning', documentNumber: invoice.number, party,
        message: 'Keine Kostenstelle gesetzt – die Auswertung beim Steuerberater wird ungenauer.',
      });
    }
  }

  return issues;
}

export function exportErrors(issues: readonly ExportIssue[]): readonly ExportIssue[] {
  return issues.filter((issue) => issue.severity === 'error');
}

export function mayExport(issues: readonly ExportIssue[]): boolean {
  return exportErrors(issues).length === 0;
}

// ---------------------------------------------------------------------------
// Kontrollblatt — the totals that prove the handover is complete.
// ---------------------------------------------------------------------------

export interface ExportReconciliation {
  readonly rowCount: number;
  /** Sum of the Debitor rows (Haben rows, i.e. credit notes, subtract). */
  readonly debitorTotal: number;
  /** Gross total of the exported outgoing documents, credit notes deducted. */
  readonly invoiceTotal: number;
  readonly debitorDifference: number;
  readonly kreditorTotal: number;
  readonly incomingTotal: number;
  readonly kreditorDifference: number;
  readonly ok: boolean;
}

/**
 * Reconciles the rows against the documents they came from. Checking "Soll = Haben" would prove
 * nothing here — each row carries Konto AND Gegenkonto, so it balances by construction. What can
 * actually go wrong is a row getting lost or duplicated, and only this comparison catches that.
 * The previous export silently dropped incoming invoices that had no Kontierung; here they make
 * the difference visible instead of disappearing.
 */
export function reconcileExport(
  rows: readonly BookingRow[],
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
): ExportReconciliation {
  const signed = (row: BookingRow) => (row.sollHaben === 'S' ? row.umsatz : -row.umsatz);
  const debitorTotal = round2(rows.filter((row) => row.richtung === 'Debitor').reduce((sum, row) => sum + signed(row), 0));
  const kreditorTotal = round2(rows.filter((row) => row.richtung === 'Kreditor').reduce((sum, row) => sum + row.umsatz, 0));

  const invoiceTotal = round2(exportableInvoices(invoices)
    .reduce((sum, invoice) => sum + invoice.grossTotal * (invoice.kind === 'credit_note' ? -1 : 1), 0));
  const incomingTotal = round2(exportableIncoming(incoming).reduce((sum, invoice) => sum + invoice.grossAmount, 0));

  const debitorDifference = round2(debitorTotal - invoiceTotal);
  const kreditorDifference = round2(kreditorTotal - incomingTotal);
  return {
    rowCount: rows.length,
    debitorTotal,
    invoiceTotal,
    debitorDifference,
    kreditorTotal,
    incomingTotal,
    kreditorDifference,
    ok: Math.abs(debitorDifference) < 0.02 && Math.abs(kreditorDifference) < 0.02,
  };
}
